// src/components/shared/GatewayModal.jsx
import React, { useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Building2, ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setUserType } from "../../utils/userType";

const ACCENT = "#2563EB";
const ACCENT_SOFT = "#EFF4FF";

// In-memory flag stored on `window`, deliberately NOT sessionStorage/localStorage.
// - A full page refresh recreates `window` from scratch, so this resets to
//   undefined and the modal shows again, per your requirement.
// - Client-side navigation to another route and back does NOT touch `window`,
//   so the flag stays true and the modal won't reopen just because HomePage
//   remounted. Using `window` (rather than a module-level variable) also
//   guarantees a single shared value even if the bundler splits this file
//   into more than one chunk.
const FLAG = "__gatewayModalShown";

let hasShownThisLoad = typeof window !== "undefined" && window[FLAG] === true;

const setShown = () => {
  hasShownThisLoad = true;
  if (typeof window !== "undefined") window[FLAG] = true;
};

const visitorCards = [
  {
    type: "student",
    href: "/",
    title: "I am a student",
    icon: GraduationCap,
  },
  {
    type: "business",
    href: "/",
    title: "I am a business",
    icon: Building2,
  },
];

export default function GatewayModal() {
  const [open, setOpen] = useState(!hasShownThisLoad);
  const navigate = useNavigate();

  const handleSelect = (card) => {
    // Sync the header's student/business mode — this also flips which of
    // "All Courses" / "Business" shows in the nav, since Header listens for
    // this change via the shared useUserType hook.
    setUserType(card.type);
    setShown();
    setOpen(false);
    navigate(card.href);
  };

  return (
    <AnimatePresence>
      {open && (
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="gateway-modal-title"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          {/* Blurred backdrop over the live homepage behind it.
              Intentionally NOT clickable to dismiss — it still sits on top
              (position: absolute, inset: 0) so clicks land on it rather than
              passing through to the page behind, they just don't do anything.
              The modal can only be closed by selecting one of the two cards. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(11, 21, 48, 0.45)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          />

          {/* Modal card */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{ position: "relative", width: "100%", maxWidth: 480 }}
          >
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                width: "100%",
                borderRadius: { xs: "18px", md: "24px" },
                bgcolor: "white",
                border: "1px solid rgba(37,99,235,0.12)",
                boxShadow: "0 30px 70px -25px rgba(11,21,48,0.35)",
                p: { xs: 3, md: 4 },
              }}
            >
              {/* <Box
                onClick={handleClose}
                role="button"
                aria-label="Close"
                sx={{
                  position: "absolute",
                  top: { xs: 12, md: 16 },
                  right: { xs: 12, md: 16 },
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: ACCENT_SOFT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#E0EBFF" },
                }}
              >
                <X size={18} color={ACCENT} />
              </Box> */}

              <Typography
                id="gateway-modal-title"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.15rem", md: "1.3rem" },
                  lineHeight: 1.25,
                  letterSpacing: "-0.01em",
                  color: "#0B1530",
                  textAlign: "center",
                  mb: { xs: 3, md: 3.5 },
                  pr: { xs: 4, md: 0 },
                }}
              >
                Who are you?
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {visitorCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <Paper
                      key={card.type}
                      onClick={() => handleSelect(card)}
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.75,
                        p: 2,
                        borderRadius: "14px",
                        border: "1px solid #E2E8F0",
                        bgcolor: "white",
                        cursor: "pointer",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 16px 32px -20px ${ACCENT}66`,
                          borderColor: ACCENT,
                        },
                        "&:hover .gw-arrow": { transform: "translateX(3px)" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: ACCENT_SOFT,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={20} color={ACCENT} strokeWidth={2} />
                      </Box>

                      <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#0B1530", flex: 1 }}>
                        {card.title}
                      </Typography>

                      <ArrowRight size={16} color={ACCENT} className="gw-arrow" style={{ transition: "transform 0.2s ease" }} />
                    </Paper>
                  );
                })}
              </Box>
            </Paper>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
}